/**
 * Staging Handlers (K6 P5 D6) — uuid-keyed.
 *
 * The card is minted OPTIMISTICALLY in `sendMessage`'s busy branch (keyed by the
 * client wireUuid) so it shows instantly; MESSAGE_STAGED is the server CONFIRM and
 * is a uuid-idempotent no-op when the optimistic card is already present (it still
 * creates the card on paths that didn't mint one, e.g. reconnect). The card is
 * removed on uuid-matched MESSAGE_PICKED_UP, fully reconciled by STAGING_SYNC
 * (replace-by-uuid; empty snapshot = recycle = clear all), and belt-and-suspenders
 * cleared on AGENT_END for any card never picked up.
 *
 * INVARIANT (level-triggered promotion): every path that clears a card threads
 * its message first — pickup, sync-drop, and turn-end alike — because the pickup
 * event is push-only and a socket flap can eat it. The one exception is the
 * redirect pickup (N staged messages merged into one fresh prompt server-side).
 */
import type { Socket } from "socket.io-client";
import { EVENTS } from "@bash/shared";
import type {
  MessageStagedPayload,
  MessagePickedUpPayload,
  StagingSyncPayload,
  DepositAckPayload,
} from "@bash/shared";
import { useTaskStore } from "../taskStore";
import type { StagedMessage, Message, DepositChip } from "../types";
import { DEFAULT_AGENT } from "../types";
import { findMessageByAnyId } from "../helpers/findMessage";
import type { ClientPayloadOf } from "../socketService";

/**
 * Phase 3 intelligent ingest — how long a "saved to your brain" deposit chip
 * lingers before it self-clears. Mirrors the staged-card belt-and-suspenders
 * cleanup philosophy.
 */
const DEPOSIT_CHIP_TTL_MS = 8000;

/**
 * Promote a staged card's message into the conversation thread (idempotent).
 * Level-triggered safety net: MESSAGE_PICKED_UP rides the live socket only, so a
 * connection flap at pickup time loses the event. Any path that clears a card
 * MUST thread its message first (the user's send is already persisted server-side;
 * dropping the card silently made the message vanish from the conversation until
 * a full reload). Sole exception: the redirect pickup (N-into-1 merge).
 */
function promoteToThread(
  s: { tasks: Map<string, { messages: Message[] }> },
  taskId: string,
  card: StagedMessage
): void {
  const task = s.tasks.get(taskId);
  if (!task) return;

  let promoted: Message | undefined = card.message;
  if (!promoted) {
    promoted = {
      id: card.uuid,
      role: "user",
      content: [{ type: "text", text: card.content }],
      timestamp: new Date(card.stagedAt),
      agentSlug: DEFAULT_AGENT,
      identity: { kind: "persisted", serverId: card.uuid },
    };
  }
  if (findMessageByAnyId(task.messages, promoted.id) === -1) {
    task.messages.push(promoted);
  }
}

export function registerStagingHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  const handleStaged = (data: MessageStagedPayload) => {
    if (!data.taskId || !data.uuid) return; // uuid-keyed: a uuid-less stage has no FE handle
    const uuid = data.uuid;
    taskStore.setState((s) => {
      // Thread-idempotent: an idle-branch send renders straight into the
      // conversation thread (no staged card). If the executor then stages that
      // same message mid-turn (turn-boundary race), MESSAGE_STAGED arrives for a
      // uuid that ALREADY has a thread bubble — minting a chip here would double-
      // render it. sendMessage stamps this uuid as `wireUuid` on the optimistic
      // message, so findMessageByAnyId can see that bubble. The message stays in
      // the thread; the executor steers it at the next tool boundary (#1496).
      const task = s.tasks.get(data.taskId);
      if (task && findMessageByAnyId(task.messages, uuid) !== -1) return;
      const list = s.stagedMessages.get(data.taskId) || [];
      if (list.some((m) => m.uuid === uuid)) return; // idempotent
      const card: StagedMessage = {
        uuid,
        content: data.content,
        taskId: data.taskId,
        origin: data.origin,
        priority: data.priority,
        stagedAt: Date.now(),
      };
      s.stagedMessages.set(data.taskId, [...list, card]);
    });
  };

  const handlePickedUp = (data: MessagePickedUpPayload) => {
    if (!data.taskId || !data.uuid) return;
    const uuid = data.uuid;

    // Two-stage interrupt: a flush-and-redirect pickup drops the staged record WITHOUT
    // threading the message — the N queued messages were merged into one combined prompt
    // that arrives separately as a fresh user turn. Promoting here would render N stray
    // bubbles on top of the merged one (the N+1 bug). Removing the card removes the whole
    // record (its `message` rides on the card), so nothing is left to resurrect.
    if (data.redirect) {
      taskStore.setState((s) => {
        const list = (s.stagedMessages.get(data.taskId) || []).filter(
          (m) => m.uuid !== uuid
        );
        if (list.length === 0) s.stagedMessages.delete(data.taskId);
        else s.stagedMessages.set(data.taskId, list);
      });
      return;
    }

    taskStore.setState((s) => {
      // Read the card before we drop it — it holds the exact in-flight Message
      // (`card.message`, for an own busy send) and the content/origin used to
      // synthesize a fallback when the card arrived without one (e.g. via
      // STAGING_SYNC after a reconnect, or a stage from another surface).
      const card = (s.stagedMessages.get(data.taskId) || []).find(
        (m) => m.uuid === uuid
      );

      // 1) Remove the tray card. This drops the WHOLE record — `card.message` goes
      // with it — so there is no second place a stale message could linger.
      const list = (s.stagedMessages.get(data.taskId) || []).filter(
        (m) => m.uuid !== uuid
      );
      if (list.length === 0) s.stagedMessages.delete(data.taskId);
      else s.stagedMessages.set(data.taskId, list);

      // 2) Promote the message into the conversation thread. Staged-only-until-
      // pickup: the message was suppressed from the thread on send, so it lands
      // here exactly once. Prefers the exact object carried on the card; falls
      // back to synthesizing from card content (foreign stage / post-reconnect
      // sync). Dedup vs a MESSAGE_SYNC that may already have threaded this row.
      if (card) promoteToThread(s, data.taskId, card);
    });
  };

  const handleSync = (data: StagingSyncPayload) => {
    if (!data.taskId) return;
    taskStore.setState((s) => {
      const prev = s.stagedMessages.get(data.taskId) ?? [];
      const snapshotUuids = new Set(data.staged.map((e) => e.uuid));
      // A local card absent from the authoritative snapshot was consumed while we
      // weren't listening (flap / recycle) — its pickup event is gone forever, so
      // thread the message here instead of dropping it record-and-all.
      for (const c of prev) {
        if (!snapshotUuids.has(c.uuid)) promoteToThread(s, data.taskId, c);
      }

      if (data.staged.length === 0) {
        s.stagedMessages.delete(data.taskId);
        return;
      } // recycle → clear the whole record set
      const now = Date.now();
      // Preserve the locally-carried Message across resync: the server snapshot has
      // no `message`, so for a uuid that is STILL staged we ride the existing card's
      // object forward — pickup then still promotes the exact object instead of
      // synthesizing. Skip snapshot entries already threaded (a late snapshot after
      // an AGENT_END promotion would otherwise re-mint a ghost chip).
      const prevByUuid = new Map(prev.map((c) => [c.uuid, c]));
      const cards = data.staged
        .filter((e) => {
          const task = s.tasks.get(data.taskId);
          return !task || findMessageByAnyId(task.messages, e.uuid) === -1;
        })
        .map((e) => {
          const carried = prevByUuid.get(e.uuid)?.message;
          return {
            uuid: e.uuid,
            content: e.content,
            taskId: data.taskId,
            origin: e.origin,
            priority: e.priority,
            stagedAt: now,
            ...(carried ? { message: carried } : {}),
          };
        });
      if (cards.length === 0) s.stagedMessages.delete(data.taskId);
      else s.stagedMessages.set(data.taskId, cards);
    });
  };

  const handleAgentEnd = (data: ClientPayloadOf<typeof EVENTS.AGENT_END>) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;
    // Fallback for never-picked-up cards: the turn is over, so the pickup event is
    // not coming (lost on a flap, or never emitted). Thread each message before
    // clearing — the send is already persisted server-side and must stay visible.
    taskStore.setState((s) => {
      for (const c of s.stagedMessages.get(taskId) ?? [])
        promoteToThread(s, taskId, c);
      s.stagedMessages.delete(taskId);
    });
  };

  // ── Phase 3 intelligent ingest — "saved to your brain" deposit chips ──
  // Server-authoritative, uuid-keyed (idempotent). A deposit produces no
  // assistant turn; the chip is the only acknowledgement. It clears after a TTL.
  const depositTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const clearDepositChips = (taskId: string) => {
    taskStore.setState((s) => {
      s.depositChips.delete(taskId);
    });
    const timer = depositTimers.get(taskId);
    if (timer) {
      clearTimeout(timer);
      depositTimers.delete(taskId);
    }
  };

  const handleDepositAck = (data: DepositAckPayload) => {
    if (!data.taskId) return;
    taskStore.setState((s) => {
      const list = s.depositChips.get(data.taskId) || [];
      // uuid-keyed idempotency (server-authoritative). A uuid-less ack is still
      // surfaced (it has no dedupe handle) but cannot be double-deduped.
      if (data.uuid && list.some((c) => c.uuid === data.uuid)) return;
      const chip: DepositChip = {
        uuid: data.uuid,
        label: data.label,
        taskId: data.taskId,
        depositedAt: Date.now(),
      };
      s.depositChips.set(data.taskId, [...list, chip]);
    });
    // The chip self-clears on a TTL (a deposit produces no follow-up event).
    const existing = depositTimers.get(data.taskId);
    if (existing) clearTimeout(existing);
    depositTimers.set(
      data.taskId,
      setTimeout(() => clearDepositChips(data.taskId), DEPOSIT_CHIP_TTL_MS)
    );
  };

  socket.on(EVENTS.MESSAGE_STAGED, handleStaged);
  socket.on(EVENTS.MESSAGE_PICKED_UP, handlePickedUp);
  socket.on(EVENTS.STAGING_SYNC, handleSync);
  socket.on(EVENTS.AGENT_END, handleAgentEnd);
  socket.on(EVENTS.DEPOSIT_ACK, handleDepositAck);

  return () => {
    socket.off(EVENTS.MESSAGE_STAGED, handleStaged);
    socket.off(EVENTS.MESSAGE_PICKED_UP, handlePickedUp);
    socket.off(EVENTS.STAGING_SYNC, handleSync);
    socket.off(EVENTS.AGENT_END, handleAgentEnd);
    socket.off(EVENTS.DEPOSIT_ACK, handleDepositAck);
    for (const timer of depositTimers.values()) clearTimeout(timer);
    depositTimers.clear();
  };
}
